import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Sticky add-to-cart bar that appears when the main buy buttons leave the viewport.
 *
 * @typedef {object} StickyAddToCartRefs
 * @property {HTMLElement} media - Product thumbnail container.
 * @property {HTMLElement} title - Product title element.
 * @property {HTMLElement} price - Product price container.
 * @property {HTMLButtonElement} button - Sticky add-to-cart button.
 * @property {HTMLElement} buttonText - Sticky button label.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCart extends Component {
  requiredRefs = ['button', 'buttonText', 'price', 'title', 'media'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {HTMLElement | null} */
  #buyButtons = null;

  connectedCallback() {
    super.connectedCallback();

    this.#buyButtons = this.#getBuyButtons();
    if (!this.#buyButtons) return;

    this.#observer = new IntersectionObserver(this.#onIntersect, {
      root: null,
      threshold: 0,
      rootMargin: '0px 0px -8px 0px',
    });
    this.#observer.observe(this.#buyButtons);

    const section = this.#getSection();
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#getSection()?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.body.classList.remove('sticky-atc-visible');
  }

  /**
   * Triggers the main product form add-to-cart button.
   */
  handleAddToCart() {
    const form = this.#getProductForm();
    if (!form) return;

    const mainButton = form.querySelector('[ref="addToCartButton"]');
    if (!(mainButton instanceof HTMLButtonElement) || mainButton.disabled) return;

    mainButton.click();
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onIntersect = (entries) => {
    const entry = entries[0];
    if (!entry) return;

    const shouldShow = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    this.#setVisible(shouldShow);
  };

  /**
   * @param {boolean} visible
   */
  #setVisible(visible) {
    this.classList.toggle('is-visible', visible);
    this.setAttribute('aria-hidden', visible ? 'false' : 'true');
    document.body.classList.toggle('sticky-atc-visible', visible);
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const html = event.detail.data.html;
    if (!html) return;

    const newButton = html.querySelector('[ref="addToCartButton"]');
    const newButtonText = newButton?.querySelector('.add-to-cart-text__content');
    if (newButtonText) {
      this.refs.buttonText.textContent = newButtonText.textContent?.trim() ?? '';
    }

    const resource = event.detail.resource;
    if (resource == null || resource.available === false) {
      this.refs.button.disabled = true;
    } else {
      this.refs.button.disabled = false;
    }

    const newPrice = html.querySelector('product-price [ref="priceContainer"]');
    if (newPrice) {
      const currentPrice = this.refs.price.querySelector('[ref="priceContainer"]');
      if (currentPrice) {
        currentPrice.replaceWith(newPrice.cloneNode(true));
      } else {
        this.refs.price.replaceChildren(newPrice.cloneNode(true));
      }
    }

    const mediaSrc = resource?.featured_media?.preview_image?.src;
    if (mediaSrc) {
      const image = this.refs.media.querySelector('img');
      if (image instanceof HTMLImageElement) {
        const url = new URL(mediaSrc, window.location.origin);
        url.searchParams.set('width', '120');
        image.src = url.toString();
        image.srcset = `${url.toString()} 120w`;
      }
    }

    const newTitle = html.querySelector(
      '.product-details h1, .product-details .product-title, [data-product-title]'
    );
    if (newTitle?.textContent) {
      this.refs.title.textContent = newTitle.textContent.trim();
    }
  };

  /** @returns {HTMLElement | null} */
  #getSection() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return null;
    return document.getElementById(`shopify-section-${sectionId}`);
  }

  /** @returns {HTMLElement | null} */
  #getBuyButtons() {
    return this.#getSection()?.querySelector('.buy-buttons-block') ?? null;
  }

  /** @returns {HTMLFormElement | null} */
  #getProductForm() {
    return (
      this.#getSection()?.querySelector('product-form-component form[data-type="add-to-cart-form"]') ?? null
    );
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
